<template>
    <div class="flex items-center">
        <div class="relative inline-flex shadow-sm rounded-md">
            <button
                :id="`cart-item-${cartItem.id}-decrease-quantity`"
                @click="decreaseCartItemQuantity(cartItem, 1, shippingTimeId)"
                type="button"
                class="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 focus:z-10 focus:outline-none"
            >
                <span class="sr-only">-1</span>
                <MinusIcon
                    v-if="cartItem.quantity > 1"
                    class="h-5 w-5"
                    aria-hidden="true"
                />
                <TrashIcon v-else class="h-5 w-5" aria-hidden="true" />
            </button>

            <Listbox class="-ml-px" as="div" v-model="selectedQuantity">
                <ListboxLabel />
                <div class="relative">
                    <ListboxButton
                        class="bg-white relative w-full border border-gray-300 px-6 py-2 text-left cursor-default focus:outline-none font-semibold text-sm text-gray-700"
                    >
                        <span class="block">{{ selectedQuantity }}</span>
                    </ListboxButton>

                    <transition
                        leave-active-class="transition ease-in duration-100"
                        leave-from-class="opacity-100"
                        leave-to-class="opacity-0"
                    >
                        <ListboxOptions
                            class="absolute z-20 mt-1 w-full bg-white border border-gray-300 shadow-lg max-h-36 rounded-md py-1 overflow-y-auto overflow-x-hidden focus:outline-none text-sm"
                        >
                            <ListboxOption
                                as="template"
                                v-for="quantity in cartItem.product_option
                                    .maximum_quantity"
                                :key="quantity"
                                :value="quantity.toString()"
                                v-slot="{ active, selected }"
                            >
                                <li
                                    :class="[
                                        active
                                            ? 'text-white bg-nutreance-green'
                                            : 'text-gray-700',
                                        'cursor-default select-none py-2 px-2 flex justify-between items-center space-x-1',
                                    ]"
                                >
                                    <span
                                        :class="[
                                            selected
                                                ? 'font-semibold'
                                                : 'font-normal',
                                        ]"
                                    >
                                        {{ quantity }}
                                    </span>

                                    <span
                                        v-if="selected"
                                        :class="[
                                            active
                                                ? 'text-white'
                                                : 'text-nutreance-green',
                                        ]"
                                    >
                                        <CheckIcon
                                            class="h-5 w-5"
                                            aria-hidden="true"
                                        />
                                    </span>
                                </li>
                            </ListboxOption>
                        </ListboxOptions>
                    </transition>
                </div>
            </Listbox>

            <button
                :id="`cart-item-${cartItem.id}-increase-quantity`"
                @click="increaseCartItemQuantity(cartItem, 1, shippingTimeId)"
                type="button"
                class="-ml-px relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 focus:z-10 focus:outline-none"
            >
                <span class="sr-only">+1</span>
                <PlusIcon class="h-5 w-5" aria-hidden="true" />
            </button>
        </div>

        <button
            v-if="cartItem.quantity > 1"
            :id="`cart-item-${cartItem.id}-delete`"
            @click="deleteCartItem(cartItem)"
            type="button"
            :class="[
                loading ? 'cursor-not-allowed' : '',
                'ml-3 flex items-center text-xs font-medium text-gray-400 hover:text-gray-500 focus:outline-none',
            ]"
        >
            <span class="sr-only">Delete</span>
            <LoadingIcon v-if="loading" class="h-5 w-5 text-gray-500" />
            <TrashIcon v-else class="h-5 w-5" aria-hidden="true" />
        </button>

        <div
            v-if="cartItem.quantity === 1 && loading"
            class="ml-3 flex items-center text-xs font-medium text-gray-400 hover:text-gray-500 focus:outline-none"
        >
            <LoadingIcon class="h-5 w-5 text-gray-500" />
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, watch } from "vue";
import {
    CheckIcon,
    MinusIcon,
    PlusIcon,
    TrashIcon,
} from "@heroicons/vue/solid";
import {
    Listbox,
    ListboxButton,
    ListboxLabel,
    ListboxOption,
    ListboxOptions,
} from "@headlessui/vue";
import { useCartItem } from "../../functions/useCartItem";
import LoadingIcon from "../LoadingIcon.vue";

// NOTE(4f): the Options-API prop declared `default: null`; in `<script setup>`
// an absent optional `number` prop is `undefined`, which is falsy exactly like
// `null`. `useCartItem` guards `shippingTimeId` with `if (shippingTimeId)`, so the
// null→undefined swap is behavior-identical and keeps the param type clean.
const props = defineProps<{
    cartItem?: any;
    shippingTimeId?: number;
}>();

const {
    loading,
    decreaseCartItemQuantity,
    deleteCartItem,
    increaseCartItemQuantity,
    updateCartItemQuantity,
} = useCartItem();
const selectedQuantity = ref(props.cartItem.quantity.toString());

watch(
    () => selectedQuantity.value,
    (quantity) => {
        updateCartItemQuantity(
            props.cartItem,
            parseInt(quantity),
            props.shippingTimeId,
        );
    },
);

watch(
    () => props.cartItem.quantity,
    (quantity) => {
        selectedQuantity.value = quantity.toString();
    },
);
</script>
